import type {ReactElement} from 'react';
import {useState} from 'react';
import type {AiPromptBoxAspectRatioSelectorProps, PinnableAspectRatio} from './ai-prompt-box-aspect-ratio-selector.types';
import {ASPECT_RATIO_SELECTOR_MODAL_PANEL_ID} from './ai-prompt-box-aspect-ratio-selector.types';
import styles from '../ai-prompt-box-footer-selector.module.scss';
import {closeFooterSelectorSmallScreenModal, getFooterSelectorIconTriggerSizing} from '../ai-prompt-box-footer-selector.library';
import {AspectRatioPopup} from './components/aspect-ratio-popup/aspect-ratio-popup';
import {getIconForAspectRatio, parseAspectRatio} from './ai-prompt-box-aspect-ratio-selector.library';
import {ClickableDiv} from '@Components/clickable-div';
import {Text, TextSize} from '@Components/text';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import {noop} from '@Utils/general.util';
import {useIsSmallerThanBreakpointWidth} from '@Hooks/useIsSmallerThanBreakpoint';
import {ScreenBreakpoints} from '@Libraries/responsiveness-library';
import {GA4EventName, GA4EventParamName, trackGA4CustomEvent} from '@Libraries/ga-events';
import OverlayTrigger from 'react-bootstrap/esm/OverlayTrigger';
import type {OverlayChildren} from 'react-bootstrap/esm/Overlay';
import Tooltip from 'react-bootstrap/esm/Tooltip';

export function AiPromptBoxAspectRatioSelector({promptBoxId}: AiPromptBoxAspectRatioSelectorProps): ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const {state, actions} = useAiPromptBox(promptBoxId);
  const isMobile = useIsSmallerThanBreakpointWidth(ScreenBreakpoints.MEDIUM);
  const pinnedAspectRatio = parseAspectRatio(state.pinnedAspectRatio);
  const iconTriggerSizing = getFooterSelectorIconTriggerSizing(state.size);

  const handleOpen = (): void => {
    setIsTooltipVisible(false);
    setIsOpen(true);
  };

  const handleClose = (): void => {
    setIsTooltipVisible(false);
    setIsOpen(false);
  };

  const handleRatioSelect = (ratio: PinnableAspectRatio | null): void => {
    if (ratio !== null) {
      trackGA4CustomEvent(GA4EventName.AICHAT_ASPECT_RATIO_CHANGED, {[GA4EventParamName.TYPE]: ratio});
    }

    actions.update({pinnedAspectRatio: ratio});
    handleClose();
    closeFooterSelectorSmallScreenModal(ASPECT_RATIO_SELECTOR_MODAL_PANEL_ID);
  };

  const getPopupContent = (): ReactElement => {
    return <AspectRatioPopup pinnedAspectRatio={pinnedAspectRatio} onRatioSelect={handleRatioSelect} />;
  };

  const getPinnedTriggerContent = (ratio: PinnableAspectRatio): ReactElement => {
    if (isMobile) {
      return <Icon icon="icon-aspect-ratio" size={IconSize.SIZE_ICON_16} />;
    }

    return (
      <>
        <Icon icon={getIconForAspectRatio(ratio)} size={IconSize.SIZE_ICON_16} />
        <Text val={ratio} size={TextSize.SM} bold />
      </>
    );
  };

  const getTrigger = (): ReactElement => {
    if (pinnedAspectRatio === null) {
      return (
        <ClickableDiv onClick={noop} className={`${styles.iconTrigger} ${iconTriggerSizing.triggerClass}`} aria-label={window.i18next.t('pmwjs_aspect_ratio')}>
          <Icon icon="icon-aspect-ratio" size={iconTriggerSizing.iconSize} />
        </ClickableDiv>
      );
    }

    return (
      <ClickableDiv onClick={noop} className={styles.pinnedTrigger} aria-label={window.i18next.t('pmwjs_aspect_ratio')}>
        {getPinnedTriggerContent(pinnedAspectRatio)}
        <Icon icon="icon-caret-down" size={IconSize.SIZE_ICON_16} />
      </ClickableDiv>
    );
  };

  const getTooltip = (): OverlayChildren => {
    return (
      <Tooltip className="spacing-m-b-2 _rounded-arrow">
        <Text val={window.i18next.t('pmwjs_design_size_label')} size={TextSize.MD} />
      </Tooltip>
    );
  };

  const getDropdown = (): ReactElement => {
    return (
      <Dropdown
        open={isOpen}
        onOpen={handleOpen}
        onClose={handleClose}
        isResumable
        doCloseOnInnerClick={false}
        showModalForSmallScreen
        isSmallScreenModalBottomSheet
        modalDisableDragToClose
        modalTitle={window.i18next.t('pmwjs_design_size_label')}
        modalShowBackButton
        modalShowCloseButton={false}
        panelIdForModal={ASPECT_RATIO_SELECTOR_MODAL_PANEL_ID}
        alignment={ALIGNMENT_TYPE.BOTTOM_START}
        position={DROPDOWN_POSITION.FIXED}
        popUpHasCustomWidth
        popupClassName={styles.popup}
        popupSpacingClass="spacing-p-3"
        selector={getTrigger()}
        selectorClassNameOnOpen={pinnedAspectRatio === null ? styles.openedIconTrigger : styles.openedPinnedTrigger}
        popup={getPopupContent()}
        modalContent={getPopupContent()}
        popupOffset={8}
      />
    );
  };

  return (
    <OverlayTrigger
      show={isTooltipVisible && !isOpen && !isMobile}
      onToggle={setIsTooltipVisible}
      overlay={getTooltip()}
      delay={{show: 200, hide: 200}}
      placement="top"
      trigger={['hover', 'focus']}
    >
      <div className={styles.overlayTriggerWrapper}>{getDropdown()}</div>
    </OverlayTrigger>
  );
}
